/**
 * TrendChart — daily-trend line/area chart for the selected metric.
 *
 * Uses `recharts` (already loaded in the admin bundle via
 * `AdminAnalyticsPage.jsx` — see Commit C decision report).
 *
 * Empty-state: renders EmptyState with the chart variant when the
 * timeseries is empty or every row's selected metric is zero — the
 * axis is skipped in favor of an intentional-looking empty caption.
 * This is honest ("no data yet") rather than a flat-line rendering
 * that looks like a load race.
 */
import React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import EmptyState from "./EmptyState";

const METRIC_LABEL = {
  visits: "Visits",
  // Feb 2026 Lane B — matches AdminAnalyticsTrafficPage KPI card
  // rename. See that file's KPI_ORDER comment for full rationale.
  unique_visitors: "Daily uniques (summed)",
  waitlist_signups: "Waitlist signups",
  contest_entries: "Contest entries",
};

export default function TrendChart({
  timeseries,
  metric,
  firstRollupDate,
}) {
  const rows = Array.isArray(timeseries) ? timeseries : [];
  const totalForMetric = rows.reduce(
    (acc, r) => acc + (r?.[metric] || 0),
    0,
  );

  if (rows.length === 0 || totalForMetric === 0) {
    return (
      <EmptyState
        firstRollupDate={firstRollupDate}
        variant="chart"
        testId="analytics-trend-chart-empty"
      />
    );
  }

  return (
    <div className="h-[280px]" data-testid="analytics-trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={rows}
          margin={{ top: 10, right: 12, bottom: 6, left: 4 }}
        >
          <defs>
            <linearGradient id="ppTrendFill" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="var(--pp-orange)"
                stopOpacity={0.35}
              />
              <stop
                offset="100%"
                stopColor="var(--pp-orange)"
                stopOpacity={0.05}
              />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--pp-border)"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tickFormatter={(d) => (typeof d === "string" ? d.slice(5) : d)}
            tick={{ fontSize: 11, fill: "var(--pp-text-soft)" }}
            stroke="var(--pp-border)"
          />
          <YAxis
            tick={{ fontSize: 11, fill: "var(--pp-text-soft)" }}
            stroke="var(--pp-border)"
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              background: "var(--pp-surface)",
              border: "1px solid var(--pp-border)",
              borderRadius: "8px",
              fontSize: "12px",
            }}
            labelStyle={{ color: "var(--pp-text)", fontWeight: 600 }}
            formatter={(v) => [v, METRIC_LABEL[metric] || metric]}
          />
          <Area
            type="monotone"
            dataKey={metric}
            stroke="var(--pp-orange)"
            fill="url(#ppTrendFill)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
